/**
 * Seedable PRNG (Mulberry32). Same seed → same sequence,
 * which makes level generation deterministic and tests reproducible.
 */
export interface Rng {
  next(): number;
  int(maxExclusive: number): number;
  pick<T>(arr: readonly T[]): T;
  chance(probability: number): boolean;
  shuffle<T>(arr: readonly T[]): T[];
}

export const createRng = (seed: number): Rng => {
  let s = seed >>> 0;
  if (s === 0) s = 1;

  const next = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    int: (maxExclusive: number) => Math.floor(next() * maxExclusive),
    pick: <T,>(arr: readonly T[]): T => {
      if (arr.length === 0) throw new Error('rng.pick: empty array');
      return arr[Math.floor(next() * arr.length)] as T;
    },
    chance: (probability: number) => next() < probability,
    shuffle: <T,>(arr: readonly T[]): T[] => {
      const out = [...arr];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [out[i], out[j]] = [out[j] as T, out[i] as T];
      }
      return out;
    },
  };
};
